import * as THREE from 'three';
import { POIShape } from './POIShape';
import { SceneSettingsData } from '../core/SceneSettingsData';
import { AppSettingsData } from '../core/AppSettingsData';
import { Scene } from '../core/Scene';
import { CSS3DObject } from 'three/examples/jsm/renderers/CSS3DRenderer.js';
import { POI } from '../poi/POI';

/**
 * YoutubeShape - CSS3D-based plane that renders a YouTube video
 * - Uses CSS3DObject (iframe) positioned using the mesh's world transform
 * - Scales iframe pixel size to match plane world size projected to screen
 */
export class YoutubeShape extends POIShape {
  private _css3d: CSS3DObject | null = null;
  private _container: HTMLDivElement | null = null; // wrapper for border-radius masking
  private _iframe: HTMLIFrameElement | null = null;
  private _animationId: number | null = null;

  constructor(
    poi: POI,
    sceneSettings?: SceneSettingsData,
    appSettings?: AppSettingsData
  ) {
    super(poi, sceneSettings, appSettings);
  }

  protected override createGeometry(): THREE.BufferGeometry {
    // Simple unit plane (width=1, height=1). Scaling is applied via transform/layouter
    const width = 1;
    const height = 1;
    return new THREE.PlaneGeometry(width, height, 1, 1);
  }

  protected override buildMesh(): void {
    // Build a minimal WebGL mesh (invisible) just to carry transform/matrixWorld
    this._geometry = this.createGeometry();
    const invisibleMaterial = new THREE.MeshBasicMaterial({ visible: false });
    this._mesh = new THREE.Mesh(this._geometry, invisibleMaterial);
    this._mesh.name = `YoutubeShapeMesh_${this.poi.name}`;

    this.onAttachedToScene();
  }

  private onAttachedToScene(): void {
    // Create CSS3D iframe
    const uriArr = this.poi.poidata.uri.value || [];
    const url = uriArr[0] || '';
    const videoId = this.extractYouTubeVideoId(url);
    if (!videoId) {
      console.warn(`[YoutubeShape] Invalid YouTube URL for POI '${this.poi.name}': '${url}'`);
      return;
    }

    // Build wrapper div to allow corner radius masking
    const container = document.createElement('div');
    container.style.position = 'absolute';
    container.style.overflow = 'hidden';
    container.style.background = 'transparent';

    const iframe = document.createElement('iframe');
    iframe.style.border = 'none';
    iframe.style.width = '100%';
    iframe.style.height = '100%';
    iframe.allow = 'autoplay; encrypted-media; fullscreen; picture-in-picture';
    iframe.setAttribute('allowfullscreen', '');
    iframe.referrerPolicy = 'origin-when-cross-origin';
    iframe.src = this.buildEmbedUrl(videoId);

    container.appendChild(iframe);

    const css = new CSS3DObject(container);
    css.name = `YouTube_${videoId}`;
    css.matrixAutoUpdate = false; // we'll copy matrixWorld each frame
    this._css3d = css;
    this._container = container;
    this._iframe = iframe;

    Scene.Instance.addCSS3DObject(css);

    // Start sync loop
    this.startSyncLoop();
  }

  private extractYouTubeVideoId(url: string): string | null {
    const patterns = [
      /(?:https?:\/\/)?(?:www\.)?youtube\.com\/watch\?v=([^&\n?#]+)/,
      /(?:https?:\/\/)?(?:www\.)?youtube\.com\/embed\/([^&\n?#]+)/,
      /(?:https?:\/\/)?(?:www\.)?youtu\.be\/([^&\n?#]+)/,
    ];
    for (const p of patterns) {
      const m = url.match(p);
      if (m) return m[1];
    }
    return null;
  }

  private buildEmbedUrl(videoId: string): string {
    const params = new URLSearchParams();
    params.set('enablejsapi', '1');
    params.set('origin', window.location.origin);
    params.set('playsinline', '1');
    params.set('autoplay', '1');
    params.set('mute', '1');
    params.set('loop', '1');
    params.set('playlist', videoId);
    params.set('modestbranding', '1');
    params.set('rel', '0');
    return `https://www.youtube-nocookie.com/embed/${videoId}?${params.toString()}`;
  }

  private startSyncLoop(): void {
    const step = () => {
      try {
        this.syncCss3dTransformAndSize();
      } finally {
        this._animationId = requestAnimationFrame(step);
      }
    };
    this._animationId = requestAnimationFrame(step);
  }

  private syncCss3dTransformAndSize(): void {
    if (!this._css3d || !this._mesh) return;
    const scene = Scene.Instance;
    const camera = scene.getCamera();
    const renderer = scene.getRenderer();

    // 1) Copy world transform (position+rotation) into CSS3DObject
    this._mesh.updateMatrixWorld(true);
    this._css3d.matrix.copy(this._mesh.matrixWorld);
    this._css3d.matrix.decompose(this._css3d.position, this._css3d.quaternion, this._css3d.scale);

    // Force neutral 3D scale, we drive size by CSS pixels
    this._css3d.scale.set(1, 1, 1);

    // 2) Compute world size (x/y) from geometry bounding box
    const geom = this._geometry as THREE.BufferGeometry;
    if (!geom) return;
    if (!geom.boundingBox) geom.computeBoundingBox();
    const bbox = geom.boundingBox!;

    // transform bbox size by world scale
    const worldScale = new THREE.Vector3();
    this._mesh.matrixWorld.decompose(new THREE.Vector3(), new THREE.Quaternion(), worldScale);
    const widthWorld = (bbox.max.x - bbox.min.x) * worldScale.x;
    const heightWorld = (bbox.max.y - bbox.min.y) * worldScale.y;

    // 3) Compute px-per-unit from camera
    // Distance along view Z of the object
    const objWorldPos = new THREE.Vector3();
    this._mesh.getWorldPosition(objWorldPos);
    const viewMatrix = camera.matrixWorldInverse.clone();
    const viewPos = objWorldPos.clone().applyMatrix4(viewMatrix);
    const z = -viewPos.z; // positive in front of camera

    const fovRad = THREE.MathUtils.degToRad(camera.fov);
    const worldHeightAtZ = 2 * z * Math.tan(fovRad / 2);
    const pxPerUnit = renderer.domElement.clientHeight / worldHeightAtZ;

    // 4) Apply pixel size to iframe
    const pxW = Math.max(1, Math.round(widthWorld * pxPerUnit));
    const pxH = Math.max(1, Math.round(heightWorld * pxPerUnit));
    // Set container pixel size (iframe is 100% fill)
    const container = this._container!;
    container.style.width = `${pxW}px`;
    container.style.height = `${pxH}px`;

    // Corner radius from POI data (in world units), map to pixels
    const corner = this.poi.poidata.cornerRadius?.value ?? 0;
    if (corner > 0) {
      const radiusPx = Math.max(0, Math.round(corner * pxPerUnit));
      container.style.borderRadius = `${radiusPx}px`;
    } else {
      container.style.borderRadius = '0px';
    }
  }

  public override dispose(): void {
    if (this._animationId) cancelAnimationFrame(this._animationId);
    if (this._css3d) {
      Scene.Instance.removeCSS3DObject(this._css3d);
      this._css3d = null;
    }
    this._iframe = null;
    super.dispose();
  }
}

